import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import AnimatedHero from '../components/AnimatedHero'
import CalendlyButton from '../components/CalendlyButton'
import SEO from '../components/SEO'
import WebDevelopmentIcon from '../components/icons/WebDevelopmentIcon'
import CloudIcon from '../components/icons/CloudIcon'
import ConsultingIcon from '../components/icons/ConsultingIcon'
import SupportIcon from '../components/icons/SupportIcon'
import AIIcon from '../components/icons/AIIcon'
import Web3Icon from '../components/icons/Web3Icon'
import SpeedIcon from '../components/icons/SpeedIcon'
import TargetIcon from '../components/icons/TargetIcon'
import PartnershipIcon from '../components/icons/PartnershipIcon'
import { generateWebsiteStructuredData, generateFAQSchema, generateLocalBusinessSchema } from '../lib/structured-data-utils'
import { bandTone, TONE_CLASSES } from '../lib/section-tones'

const Home = () => {
  const services = [
    {
      id: 'development',
      title: 'Development',
      description: 'Modern, responsive websites and web applications built with the latest technologies.',
      icon: WebDevelopmentIcon,
    },
    {
      id: 'cloud',
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and migration services for growing businesses.',
      icon: CloudIcon,
    },
    {
      id: 'consulting',
      title: 'IT Consulting',
      description: 'Strategic technology consulting to optimize your business operations.',
      icon: ConsultingIcon,
    },
    {
      id: 'support',
      title: 'Support & Maintenance',
      description: '24/7 technical support and maintenance for your digital infrastructure.',
      icon: SupportIcon,
    },
    {
      id: 'ai',
      title: 'AI',
      description: 'Cutting-edge AI solutions to automate processes and unlock insights.',
      icon: AIIcon,
    },
    {
      id: 'web3',
      title: 'Web3 & Blockchain',
      description: 'Decentralized applications and smart contract development.',
      icon: Web3Icon,
    },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  const faqs = [
    {
      question: 'What services does Demirix offer?',
      answer: 'Demirix offers comprehensive IT solutions including web development, cloud migration, AI integration, Web3/blockchain development, IT consulting, and 24/7 technical support. We specialize in helping businesses undergo digital transformation with cutting-edge technology solutions.'
    },
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on scope and complexity. Simple websites can be completed in 2-4 weeks, while complex enterprise applications may take 3-6 months. We provide detailed timelines during our initial consultation.'
    },
    {
      question: 'Do you offer ongoing support after project completion?',
      answer: 'Yes, we provide comprehensive support and maintenance packages including 24/7 monitoring, security updates, performance optimization, and technical support to ensure your systems run smoothly.'
    },
    {
      question: 'What technologies do you work with?',
      answer: 'We work with modern technologies including React, Node.js, Python, AWS, Azure, Kubernetes, Docker, and blockchain platforms like Ethereum and Solana. We choose the best technology stack based on your project requirements.'
    },
    {
      question: 'Do you work with international clients?',
      answer: 'Yes, we work with clients worldwide. Based in Dubai, we serve businesses globally with remote collaboration tools and flexible communication schedules to accommodate different time zones.'
    }
  ]

  const structuredData = [
    generateWebsiteStructuredData(),
    generateFAQSchema(faqs),
    generateLocalBusinessSchema()
  ]

  return (
    <div className="pt-20">
      <SEO
        title="Web Development & IT Consulting Dubai | Demirix"
        description="Expert web development Dubai and IT consulting Dubai services. Transform your business with cutting-edge AI solutions, cloud services, and blockchain development in UAE. Schedule your free consultation today."
        keywords={['IT consulting Dubai', 'web development', 'cloud solutions', 'AI solutions', 'Web3 blockchain', 'startup technology consulting', 'digital transformation']}
        url="https://demirix.com/"
        structuredData={structuredData}
      />
      <AnimatedHero />
      
      <section className="section-padding bg-white dark:bg-gray-900 transition-colors duration-200">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="text-center mb-16"
          >
            <motion.h2
              variants={itemVariants}
              className="text-h2 text-gray-900 dark:text-white mb-6"
            >
              What We Do Best
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-body-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
            >
              We specialize in delivering comprehensive IT solutions powered by AI and modern technologies to help businesses thrive in the digital age.
              Learn more about our <Link to="/services" className="text-primary-600 dark:text-primary-400 hover:underline font-semibold">comprehensive services</Link> and
              let's discuss how we can transform your technology challenges into competitive advantages.
            </motion.p>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
            >
            {services.map((service) => {
              const IconComponent = service.icon
              return (
                <motion.div
                  key={service.id}
                  variants={itemVariants}
                >
                  <Link
                    to={`/services?service=${service.id}`}
                    className="block bg-gray-50 dark:bg-gray-800 p-6 rounded-xl hover:bg-white dark:hover:bg-gray-700 hover:shadow-lg transition-all duration-300 group text-center h-full"
                  >
                    <div className="mb-4 flex justify-center">
                      <IconComponent className="w-16 h-16 text-primary-600 group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <h3 className="text-h5 text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      {service.description}
                    </p>
                    <div className="mt-4 text-primary-600 dark:text-primary-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      View {service.title} details →
                    </div>
                  </Link>
                </motion.div>
              )
            })}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50 dark:bg-gray-800 transition-colors duration-200">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={itemVariants}
              className="text-h2 text-gray-900 dark:text-white mb-8 text-center"
            >
              Technology Solutions Built for Growth
            </motion.h2>
            <motion.div variants={itemVariants} className="space-y-6 text-body text-gray-600 dark:text-gray-300">
              <p>
                Demirix Technology LLC is a Dubai-based IT consultancy that partners with startups, SMEs, and enterprises to build software that drives real business results. From custom web applications and cloud-native architectures to AI-powered automation and DevSecOps pipelines, we deliver end-to-end technology solutions tailored to the demands of the UAE market and beyond.
              </p>
              <p>
                Our engineering team specializes in modern frameworks like React, TypeScript, and Node.js, paired with cloud platforms including AWS, Azure, and Google Cloud. Whether you need to migrate legacy systems to the cloud, implement Kubernetes-based container orchestration, or integrate machine learning into your workflows, we bring the technical depth and regional expertise to make it happen.
              </p>
              <p>
                Based in Dubai and serving clients across Abu Dhabi, the wider UAE, and internationally, we understand the unique challenges businesses face in this region. From regulatory compliance and data residency requirements to rapid scaling during peak demand, our solutions are designed with these realities in mind. Explore our <Link to="/dubai-services" className="text-primary-600 dark:text-primary-400 hover:underline font-semibold">Dubai web development services</Link> or our <Link to="/abu-dhabi-services" className="text-primary-600 dark:text-primary-400 hover:underline font-semibold">Abu Dhabi technology solutions</Link>. We take a partnership approach to every engagement, working closely with your team from initial architecture through deployment and ongoing support.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white dark:bg-gray-900 transition-colors duration-200">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h2
              variants={itemVariants}
              className="text-h2 text-gray-900 dark:text-white mb-8"
            >
              Explore Our Services
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-body-lg text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto"
            >
              From DevOps and cloud infrastructure to custom software and e-commerce, we deliver specialized solutions for businesses in Dubai and across the UAE.
            </motion.p>
            <motion.div
              variants={containerVariants}
              className="grid grid-cols-2 md:grid-cols-3 gap-4"
            >
              {[
                { to: '/devops-services-dubai', label: 'DevOps Services' },
                { to: '/software-development-dubai', label: 'Software Development' },
                { to: '/aws-cloud-consulting-dubai', label: 'AWS Cloud Consulting' },
                { to: '/kubernetes-consulting-dubai', label: 'Kubernetes Consulting' },
                { to: '/it-consulting-companies-dubai', label: 'IT Consulting Dubai' },
                { to: '/managed-it-services-dubai', label: 'Managed IT Services' },
              ].map((service) => (
                <motion.div key={service.to} variants={itemVariants}>
                  <Link
                    to={service.to}
                    className="block bg-gray-50 dark:bg-gray-800 px-4 py-3 rounded-lg hover:bg-primary-50 dark:hover:bg-gray-700 hover:shadow-md transition-all duration-300 text-gray-900 dark:text-white font-medium text-sm hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    {service.label}
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className={`section-padding ${TONE_CLASSES[bandTone('/')]} text-gray-900 dark:text-white transition-colors duration-200`}>
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="text-center"
          >
            <motion.h2
              variants={itemVariants}
              className="text-h2 mb-8"
            >
              Ready to Transform Your Business?
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-xl text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto"
            >
              Every great project starts with a conversation. Let's discuss your goals,
              challenges, and how we can create the perfect solution for your needs.
            </motion.p>
            <motion.div
              variants={itemVariants}
              className="flex justify-center items-center"
            >
              <CalendlyButton 
                variant="outline" 
                className="transform hover:scale-105"
              >
                Schedule Your Free Consultation
              </CalendlyButton>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white dark:bg-gray-900 transition-colors duration-200">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h2 
              variants={itemVariants}
              className="text-h2 text-gray-900 dark:text-white mb-8"
            >
              Why Choose Demirix?
            </motion.h2>
            <motion.div 
              variants={containerVariants}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SpeedIcon className="w-12 h-12 text-primary-600" />
                </div>
                <h3 className="text-h5 text-gray-900 dark:text-white mb-3">Fast Delivery</h3>
                <p className="text-body text-gray-600 dark:text-gray-300">We understand time is money. Our agile approach ensures rapid delivery without compromising quality.</p>
              </motion.div>
              
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <TargetIcon className="w-12 h-12 text-primary-600" />
                </div>
                <h3 className="text-h5 text-gray-900 dark:text-white mb-3">Tailored Solutions</h3>
                <p className="text-body text-gray-600 dark:text-gray-300">No cookie-cutter approaches. Every solution is crafted specifically for your unique requirements.</p>
              </motion.div>
              
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <PartnershipIcon className="w-12 h-12 text-primary-600" />
                </div>
                <h3 className="text-h5 text-gray-900 dark:text-white mb-3">Partnership Approach</h3>
                <p className="text-body text-gray-600 dark:text-gray-300">We're not just vendors, we're your technology partners committed to your long-term success.</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home